/**
 * Compose Message Modal - Send encrypted messages to studios or visitors
 */

import React, { useState } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import * as messagesApi from '../../lib/mudoNetwork/messagesApi';

export default function ComposeMessageModal({ 
  isOpen, 
  onClose, 
  recipientId, 
  productId = null, 
  workshopId = null,
  productTitle = null,
  onSuccess 
}) {
  const [subject, setSubject] = useState(productTitle ? `Inquiry about: ${productTitle}` : '');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const handleSend = async () => {
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    if (!recipientId) {
      setError('Recipient not specified');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const result = await messagesApi.sendMessage({
        recipientId,
        content: message,
        subject: subject || 'New message',
        productId,
        workshopId,
      });

      if (result.error) {
        setError(result.error.message || 'Failed to send message');
        return;
      }

      // Success
      if (onSuccess) onSuccess();
      onClose();
      
      // Reset form
      setSubject('');
      setMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      setError('An error occurred while sending the message');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#1e1f22] border border-white/10 rounded-2xl shadow-2xl w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">New Message</h2>
          <button
            onClick={onClose}
            disabled={sending}
            className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          {/* Subject */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject"
              disabled={sending}
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500/50 disabled:opacity-50"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              disabled={sending}
              rows={8}
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500/50 resize-none disabled:opacity-50"
            />
          </div>

          {/* Info Note */}
          {productTitle && (
            <div className="p-3 rounded-lg bg-blue-600/10 border border-blue-500/20">
              <p className="text-sm text-blue-300">
                💬 Regarding: <span className="font-semibold">{productTitle}</span>
              </p>
            </div>
          )}

          {/* Encryption Note */}
          <div className="p-3 rounded-lg bg-green-600/10 border border-green-500/20">
            <p className="text-xs text-green-300">
              🔒 All messages are encrypted for your privacy
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-red-600/20 border border-red-500/30">
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
          <button
            onClick={onClose}
            disabled={sending}
            className="px-5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-white font-semibold transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !message.trim()}
            className="px-5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Message
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
